import { FC, Reducer, useEffect, useReducer, useRef, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isEmpty, isEqual, isNumber, omit } from 'lodash';
import queryString from 'query-string';

import { SearchContext, SearchContextProps } from '@app/contexts';
import {
  useInstanceDetailsRoutePath,
  usePaging,
  useQueryParams,
} from '@app/hooks';
import { DateRange, generateRouteParams } from '@app/utils';
import {
  RECORDS_PER_PAGE,
  ROUTE,
  SORT_DIRECTION,
  URLQuery,
} from '@app/constants';

import {
  FACETS_ORDER,
  SEARCH_OPTION,
  SearchInstanceDto,
  SearchResultsDto,
  SearchResultsErrorsDto,
  SearchState,
  SelectedFacetAttributes,
  SORT_FIELD,
} from '../constants';
import { useSearchRequest, useUrlManager } from '../hooks';
import { formatSelectedFacets } from '../utils';

export type SearchStateInitialized = SearchState &
  Required<
    Pick<
      SearchState,
      'isInnerUpdate' | 'query' | 'pageNumber' | 'recordsPerPage'
    >
  >;

type SearchProviderProps = {
  searchContext?: Partial<SearchContextProps>;
};

export const SearchProvider: FC<SearchProviderProps> = ({
  children,
  searchContext,
}) => {
  const [totalRecords, setTotalRecords] = useState<number>();
  const [searchOption, setSearchOption] = useState<SEARCH_OPTION>(
    SEARCH_OPTION.keyword
  );
  const [selectedFacets, setSelectedFacets] = useState<SelectedFacetAttributes>(
    {}
  );
  const [selectedPublicationYear, setSelectedPublicationYear] =
    useState<DateRange>({});
  const [instanceIdScrollTo, setInstanceIdScrollTo] = useState(null);
  const [isPageFromBackButton, setIsPageFromBackButton] = useState(false);

  const initialSearchState = {
    pageNumber: 1,
    query: '',
    sortDirection: SORT_DIRECTION.asc,
    sortField: SORT_FIELD.relevance,
    option: SEARCH_OPTION.keyword,
    facets: {},
    publicationYear: {},
    isInnerUpdate: true,
    isNewPageRequest: false,
    recordsPerPage: RECORDS_PER_PAGE,
  };

  const [state, updateState] = useReducer<
    Reducer<SearchStateInitialized, Partial<SearchState>>
  >(
    (prevState, newState) => ({
      ...prevState,
      ...newState,
      pageNumber: newState.pageNumber || 1,
      isInnerUpdate: Boolean(newState.isInnerUpdate),
      isNewPageRequest: Boolean(newState.isNewPageRequest),
    }),
    initialSearchState
  );

  const [isStatePristine, setIsStatePristine] = useState(true);
  const { getSearchStateFromUrl } = useUrlManager();
  const { pathname, search: urlSearchQuery } = useLocation();
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const isMountedRef = useRef(false);
  const searchQueryRef = useRef('');
  const shouldRefocusRef = useRef(false);
  const { checkIsInstanceDetailsRoute } = useInstanceDetailsRoutePath();
  const isSearchRoute = pathname === ROUTE.search;

  const [searchResults, setSearchResults] = useState<SearchInstanceDto[][]>([]);
  const fetchSearch = useSearchRequest(state);
  const searchResultsQuery = useQuery<SearchResultsDto, SearchResultsErrorsDto>(
    ['searchResults', omit(state, 'isInnerUpdate', 'isNewPageRequest')],
    fetchSearch,
    {
      cacheTime: 0,
      enabled: state.isNewPageRequest
        ? isEmpty(searchResults[state.pageNumber - 1])
        : !isStatePristine &&
          (Boolean(state.query) ||
            !isEmpty(state.facets) ||
            !isEmpty(state.publicationYear)),
    }
  );

  useEffect(() => {
    const newTotalRecords = searchResultsQuery.data?.totalRecords;

    if (isNumber(newTotalRecords)) {
      setTotalRecords(newTotalRecords);

      if (newTotalRecords === 1 && isSearchRoute) {
        navigate(
          `${generatePath(ROUTE.instanceDetails, {
            id: searchResultsQuery.data?.instances?.[0].id as string,
          })}${urlSearchQuery}`
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultsQuery.data?.totalRecords]);

  useEffect(() => {
    setSearchOption(state.option);
  }, [state.option]);

  useEffect(() => {
    const isQueryUpdatedFromSearchBar = state.query && !state.isInnerUpdate;
    const isSearchFromSearchResults =
      isSearchRoute &&
      (!isEmpty(state.facets) ||
        !isEmpty(state.publicationYear) ||
        state.query);

    // isMounted makes sure that useQuery doesn't fire before the actual changes to search state were made.
    if (isMountedRef.current) {
      if (
        isStatePristine &&
        (isSearchFromSearchResults || isQueryUpdatedFromSearchBar)
      ) {
        setIsStatePristine(false);
      }

      if (
        !isStatePristine &&
        isQueryUpdatedFromSearchBar &&
        searchQueryRef.current === state.query
      ) {
        searchResultsQuery.refetch();
      }

      searchQueryRef.current = state.query;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchRoute, isStatePristine, state]);

  useEffect(() => {
    // Reset isInnerUpdate after every search fetch to avoid re-fetching results after navigating away from search results page
    updateState({
      isInnerUpdate: true,
      pageNumber: state.pageNumber,
      isNewPageRequest: state.isNewPageRequest,
    });

    // eslint-disable-next-line
  }, [searchResultsQuery.dataUpdatedAt, searchResultsQuery.errorUpdatedAt]);

  const syncUrlSelectedFacets = updatedFacets => {
    setSelectedFacets(() =>
      FACETS_ORDER.reduce((result, facetKey) => {
        const selectedFacetsForKey = updatedFacets[facetKey]?.reduce(
          (newSelectedFacets, currentFacet) => {
            newSelectedFacets[currentFacet] = true;

            return newSelectedFacets;
          },
          {}
        );

        result[facetKey] = { ...selectedFacetsForKey };

        return result;
      }, {})
    );
  };

  const updateSearchStateFromUrl = (
    locationSearchQuery?: queryString.ParsedQuery
  ) => {
    const {
      query: updatedQuery = '',
      option: updatedOption = SEARCH_OPTION.keyword,
      sortField: updatedSortField = SORT_FIELD.relevance,
      sortDirection: updatedSortDirection = SORT_DIRECTION.asc,
      facets: updatedFacets = {},
      publicationYear: updatedPublicationYear = {},
      pageNumber: updatedPageNumber = 1,
      recordsPerPage: updatedRecordsPerPage = RECORDS_PER_PAGE,
    } = getSearchStateFromUrl(locationSearchQuery);

    const newState = {
      query: updatedQuery,
      facets: updatedFacets,
      isInnerUpdate: true,
      isNewPageRequest: state.isNewPageRequest,
      publicationYear: updatedPublicationYear,
    };

    syncUrlSelectedFacets(updatedFacets);
    setSearchOption(updatedOption);
    setSelectedPublicationYear(
      isEmpty(updatedPublicationYear)
        ? updatedPublicationYear
        : {
            minDate: updatedPublicationYear.minDate ?? '',
            maxDate: updatedPublicationYear.maxDate ?? '',
          }
    );

    if (
      updatedQuery ||
      !isEmpty(updatedFacets) ||
      !isEmpty(updatedPublicationYear)
    ) {
      Object.assign(newState, {
        option: updatedOption,
        sortField: updatedSortField,
        sortDirection: updatedSortDirection,
        pageNumber: Number(updatedPageNumber),
        recordsPerPage: Number(updatedRecordsPerPage),
      });
      // update the url to remove parameters: option, sortField and sortDirection after page reload
      // as search request depends only on query and facets parameters in search state
    } else if (isSearchRoute) {
      navigate(
        generateRouteParams({
          pathname: ROUTE.search,
          query: {},
        })
      );
    }

    updateState(newState);
    searchQueryRef.current = updatedQuery;
  };

  useEffect(() => {
    isMountedRef.current = true;

    updateSearchStateFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (pathname === ROUTE.search || checkIsInstanceDetailsRoute(pathname)) &&
      state.isInnerUpdate
    ) {
      if (urlSearchQuery && urlSearchQuery !== '?') {
        updateSearchStateFromUrl(queryString.parse(urlSearchQuery));
      } else {
        syncUrlSelectedFacets({});
        setSelectedPublicationYear({});
        setSearchOption(SEARCH_OPTION.keyword);
        updateState({ ...initialSearchState, isInnerUpdate: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchQuery]);

  usePaging<SearchInstanceDto>({
    areRecordsFetched: searchResultsQuery.isSuccess,
    pageNumber: state.pageNumber,
    isNewPageRequest: Boolean(state.isNewPageRequest),
    records: searchResultsQuery.data?.instances,
    setPages: setSearchResults,
  });

  const updateFacets = (updatedFacets, key: 'facets' | 'publicationYear') => {
    const urlState = getSearchStateFromUrl();

    if (!isEqual(updatedFacets, state[key]) && isMountedRef.current) {
      // remove empty facets from the URL if last facet was unchecked manually
      if (isEmpty(updatedFacets) && queryParams[key]) {
        let updatedQueryParams = queryParams;

        if (!queryParams.query) {
          updatedQueryParams = {
            ...omit(
              updatedQueryParams,
              'option',
              'pageNumber',
              'recordsPerPage'
            ),
          };
        }

        updatedQueryParams = {
          ...omit(updatedQueryParams, key),
        };

        navigate(
          generateRouteParams({
            pathname: ROUTE.search,
            query: updatedQueryParams as URLQuery,
          })
        );
      } else {
        updateState({
          [key]: updatedFacets,
          option: searchOption,
          isInnerUpdate: isEqual(urlState[key] ?? {}, updatedFacets),
        });
      }
    }
  };

  useEffect(() => {
    const formattedFacets = formatSelectedFacets(selectedFacets);

    updateFacets(formattedFacets, 'facets');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacets, state.facets, updateState]);

  useEffect(() => {
    updateFacets(selectedPublicationYear, 'publicationYear');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPublicationYear, state.publicationYear, updateState]);

  return (
    <SearchContext.Provider
      value={{
        state,
        searchOption,
        searchResults: {
          searchResultsQuery,
          currentPage: searchResults[state.pageNumber - 1] || [],
          totalRecords,
          pages: searchResults,
        },
        shouldRefocusRef,
        selectedFacets,
        selectedPublicationYear,
        instanceIdScrollTo,
        isPageFromBackButton,
        updateState,
        setSelectedFacets,
        setSelectedPublicationYear,
        setSearchOption,
        setInstanceIdScrollTo,
        setIsPageFromBackButton,
        ...searchContext,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
